
import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft, 
  BookCheck, 
  Loader2,
  List,
  AlertTriangle,
  RefreshCw,
  CalendarIcon,
  Languages,
  Settings
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AnkiImport from "@/components/quiz/AnkiImport";
import QuestionCard from "@/components/quiz/QuestionCard";
import Results from "@/components/quiz/Results";
import { useCurrentAffairsQuestions } from "@/hooks/useCurrentAffairsQuestions";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuizSessionHistory } from "@/hooks/useQuizSessionHistory";
import { generateUUID } from "@/utils/uuid";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import { useAuthSession } from "@/hooks/useAuthSession";
import { useIsMobile } from "@/hooks/use-mobile";

const CurrentAffairsQuiz: React.FC = () => {
  const { isMobile, screenSize } = useIsMobile();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [answers, setAnswers] = useState<(number | null)[]>([]);
  const [quizComplete, setQuizComplete] = useState(false);
  const [view, setView] = useState<"intro" | "quiz" | "results">("intro");
  const [preferredDifficulty, setPreferredDifficulty] = useState<string>("medium");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [quizSessionId, setQuizSessionId] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<string | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<'english' | 'hindi' | 'hinglish'>('english');
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  const navigate = useNavigate();
  const { userId } = useAuthSession();
  
  const {
    questions,
    loading,
    error,
    refreshQuestions
  } = useCurrentAffairsQuestions(preferredDifficulty, selectedDate);
  const { resetSnapshots, resetSubmissionStatus, markQuestionAsActive } = useQuizSessionHistory();

  useEffect(() => {
    resetSubmissionStatus();
    if (view === "intro") {
      resetSnapshots();
    }
  }, [view, resetSnapshots, resetSubmissionStatus]);

  useEffect(() => {
    if (questions.length > 0) {
      setAnswers(new Array(questions.length).fill(null));
    }
  }, [questions]);

  const handleStartQuiz = () => {
    if (questions.length === 0) {
      toast.error("No questions available. Please import some Current Affairs questions first.");
      return;
    }
    
    resetSnapshots();
    resetSubmissionStatus();
    
    const newSessionId = generateUUID();
    setQuizSessionId(newSessionId);
    
    setAnswers(new Array(questions.length).fill(null));
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setQuizComplete(false);
    setView("quiz");
    
    toast.success(`Starting quiz with ${questions.length} questions`);
  };

  const handleAnswer = (selected: number) => {
    if (isTransitioning) {
      console.log("Preventing answer during transition");
      return;
    }
    
    setSelectedAnswer(selected);
    const newAnswers = [...answers];
    newAnswers[currentQuestionIndex] = selected;
    setAnswers(newAnswers);
    
    if (questions[currentQuestionIndex] && questions[currentQuestionIndex].id) {
      markQuestionAsActive(questions[currentQuestionIndex].id, true);
    }
  };

  const handleNext = () => {
    if (isTransitioning) {
      console.log("Preventing next during transition");
      return;
    }
    
    setIsTransitioning(true);
    
    setTimeout(() => {
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
        setSelectedAnswer(answers[currentQuestionIndex + 1]);
      } else {
        setQuizComplete(true);
        setView("results");
      }
      setIsTransitioning(false);
    }, 300);
  };

  const handleBackToIntro = () => {
    resetSubmissionStatus();
    setView("intro");
  };

  const handleImportSuccess = () => {
    toast.success("Questions imported successfully");
    refreshQuestions();
    setRefreshTrigger(prev => prev + 1);
  };

  const handleDifficultyChange = (value: string) => {
    setPreferredDifficulty(value);
    toast.info(`Difficulty set to ${value}`);
    refreshQuestions();
  };

  const handleLanguageChange = (value: 'english' | 'hindi' | 'hinglish') => {
    setSelectedLanguage(value);
    toast.info(`Language changed to ${value}`);
  };

  const handleDateChange = (date: Date | undefined) => {
    setSelectedDate(date);
    
    if (date) {
      toast.info(`Selected date: ${format(date, "PPP")}`, {
        description: "Quiz will show questions for this specific date"
      });
    } else {
      toast.info("Date selection cleared", {
        description: "Quiz will show latest Current Affairs questions"
      });
    }
    
    refreshQuestions();
  };

  const saveQuizResults = async () => {
    if (!userId) return;
    
    const correctAnswers = answers.reduce((count, answer, index) => {
      return count + (answer === questions[index]?.correctanswer ? 1 : 0);
    }, 0);
    
    try {
      const { error } = await supabase
        .from('current_affairs_quiz_results')
        .insert({
          user_id: userId,
          session_id: quizSessionId,
          questions: questions,
          answers: answers,
          score: correctAnswers,
          total_questions: questions.length,
          percentage: Math.round((correctAnswers / questions.length) * 100),
          difficulty: preferredDifficulty,
          date_filter: selectedDate,
          time_period: selectedTimePeriod
        });

      if (error) throw error;
      toast.success('Quiz results saved successfully!');
    } catch (err) {
      console.error('Error saving quiz results:', err);
      toast.error('Failed to save quiz results');
    }
  };

  const handleFinishQuiz = () => {
    setQuizComplete(true);
    setView("results");
    saveQuizResults();
  };

  if (view === "intro") {
    return (
      <div className={`p-2 sm:p-4 max-w-4xl mx-auto ${isMobile ? 'pb-16' : ''}`}>
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/current-affairs")}
            size={isMobile ? "sm" : "default"}
            className="text-xs sm:text-sm"
          >
            <ArrowLeft className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
            Back
          </Button>
        </div>

        <div className="text-center mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2">Current Affairs Quiz</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-6">
            Test your knowledge on the latest current affairs
          </p>
        </div>

        {error && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 border border-amber-200 bg-amber-50 rounded-lg text-amber-800 flex items-start text-xs sm:text-sm">
            <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
            <p>{error}</p>
          </div>
        )}

        <div className="grid gap-4 sm:gap-6">
          <div className="bg-card border rounded-lg p-3 sm:p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-3 sm:mb-4">
              <BookCheck className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
              <div>
                <h2 className="text-lg sm:text-xl font-semibold">Start Quiz</h2>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {questions.length > 0 
                    ? `${questions.length} questions available` 
                    : "Import questions to start the quiz"}
                </p>
              </div>
            </div>
            
            <div className="space-y-3 sm:space-y-4">
              <div className="flex flex-col space-y-3 sm:space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs sm:text-sm font-medium">Difficulty:</span>
                  <Select
                    value={preferredDifficulty}
                    onValueChange={handleDifficultyChange}
                  >
                    <SelectTrigger className="h-8 sm:h-9 w-24 sm:w-32 text-xs sm:text-sm">
                      <SelectValue placeholder="Select difficulty" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Easy</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="hard">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs sm:text-sm font-medium">Language:</span>
                  <Select
                    value={selectedLanguage}
                    onValueChange={(value: 'english' | 'hindi' | 'hinglish') => 
                      handleLanguageChange(value)
                    }
                  >
                    <SelectTrigger className="h-8 sm:h-9 w-24 sm:w-32 text-xs sm:text-sm">
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="english">English</SelectItem>
                      <SelectItem value="hindi">हिंदी</SelectItem>
                      <SelectItem value="hinglish">Hinglish</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <span className="text-xs sm:text-sm font-medium">Date:</span>
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size={isMobile ? "sm" : "default"}
                          className={cn(
                            "justify-start text-left font-normal text-xs sm:text-sm h-8 sm:h-10",
                            "w-full sm:w-[240px]",
                            !selectedDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                          {selectedDate ? format(selectedDate, "PPP") : <span>Show all dates</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <div className="p-2 flex justify-between items-center border-b">
                          <span className="text-xs sm:text-sm font-medium">Filter by date</span>
                          {selectedDate && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDateChange(undefined)}
                              className="h-7 px-2 text-xs"
                            >
                              Clear
                            </Button>
                          )}
                        </div>
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={handleDateChange}
                          initialFocus
                          className={cn("p-2 sm:p-3")}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                <Button 
                  className="flex-1 h-9 sm:h-10 text-xs sm:text-sm" 
                  onClick={handleStartQuiz}
                  disabled={loading || questions.length === 0}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-1.5 h-3 w-3 sm:h-4 sm:w-4 animate-spin" />
                      Loading...
                    </>
                  ) : questions.length > 0 ? (
                    <>
                      <List className="mr-1.5 h-3 w-3 sm:h-4 sm:w-4" />
                      Start Quiz
                    </>
                  ) : (
                    "No Questions Available"
                  )}
                </Button>
                
                <Button
                  variant="outline"
                  onClick={refreshQuestions}
                  disabled={loading}
                  className="h-9 sm:h-10 text-xs sm:text-sm"
                >
                  <RefreshCw className={`h-3 w-3 sm:h-4 sm:w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          <div className="bg-card border rounded-lg p-3 sm:p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <h2 className="text-lg sm:text-xl font-semibold">Import Questions</h2>
            </div>
            <div className="mb-3">
              <p className="text-xs sm:text-sm text-muted-foreground mb-2">
                Import questions from Anki format to build your Current Affairs quiz
              </p>
              <div className="mb-2 p-2 sm:p-3 bg-amber-50 border border-amber-200 rounded-md text-xs sm:text-sm text-amber-800">
                <strong>Important:</strong> When importing questions, ensure the Category is set to "Current Affairs" exactly.
                {selectedDate && (
                  <p className="mt-1">Questions will be imported for date: {format(selectedDate, "PPP")}</p>
                )}
              </div>
              <AnkiImport 
                subject="Current Affairs" 
                onImport={handleImportSuccess} 
                key={`anki-import-${refreshTrigger}`}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (view === "quiz" && questions.length > 0) {
    return (
      <div className={`p-2 sm:p-4 max-w-4xl mx-auto ${isMobile ? 'pb-16' : ''}`}>
        <div className="mb-4 sm:mb-6 flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={handleBackToIntro}
            size={isMobile ? "sm" : "default"}
            className="text-xs sm:text-sm"
          >
            <ArrowLeft className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
            Back
          </Button>
          
          <div className="flex items-center gap-2">
            <Select
              value={selectedLanguage}
              onValueChange={(value: 'english' | 'hindi' | 'hinglish') => 
                handleLanguageChange(value)
              }
              disabled={isTransitioning}
            >
              <SelectTrigger className="w-[100px] sm:w-[130px] h-8 sm:h-9 text-xs">
                <Settings className="h-3 w-3 text-gray-500 mr-1.5" /> 
                <SelectValue placeholder="Language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="english">English</SelectItem>
                <SelectItem value="hindi">हिंदी</SelectItem>
                <SelectItem value="hinglish">Hinglish</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="mt-2 sm:mt-4">
          <QuestionCard
            question={questions[currentQuestionIndex]}
            questionNumber={currentQuestionIndex + 1}
            totalQuestions={questions.length}
            selectedAnswer={selectedAnswer}
            onSelect={handleAnswer}
            onNext={handleNext}
            selectedLanguage={selectedLanguage}
            showBilingual={true}
            isTransitioning={isTransitioning}
          />
        </div>
      </div>
    );
  }

  if (view === "results") {
    return (
      <div className={`p-2 sm:p-4 max-w-4xl mx-auto ${isMobile ? 'pb-16' : ''}`}>
        <Button
          variant="ghost"
          onClick={handleBackToIntro}
          size={isMobile ? "sm" : "default"}
          className="text-xs sm:text-sm"
        >
          <ArrowLeft className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
          Back to Quiz
        </Button>
        
        <div className="mt-2 sm:mt-4">
          <Results 
            questions={questions} 
            answers={answers} 
            onReset={handleBackToIntro} 
            sessionId={quizSessionId}
            subject="Current Affairs"
            category="Current Affairs"
            difficulty={preferredDifficulty}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center p-6 sm:p-8">
      <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin text-primary" />
    </div>
  );
};

export default CurrentAffairsQuiz;
